/**
  * @class mmodOfficeWfDevices
  * @description
  * Devices module allows administrator to manage physical devices used within Mosy platform.
  * Device could then be assigned to particular applications.
  * One of the purposes is to register WiFi personal hotspot credentials of individual devices,
  * so e.g. mRadar could use them as a network service to connect to Mosy network infrastructure.
  * Functionalities of administration include:
  * - create new device
  * - update device
  * - disable device
  * - delete device
  * Module is implemented as workflow wfDevices.
  * Server class is msvrWfDevices, but it’s listener_id is msvrDevices.
  * It does not do any special handling, just includes workflow definitions.
  * On client side, there is mmodOfficeWfDevices module, which is derived from mmodWorkflow.
  * It does all usual workflow client processing with one small tweak - update of devices translations.
  */

import mmodWorkflow from 'mmodWorkflow'

export default class mmodOfficeWfDevices extends mmodWorkflow {
  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates an instance of the class.
  * @param {string} [id] - ID under which the object will listen for messages
  * @param {string} [cfg] - object initial configuration
  * @param {string} [cfg_schema] - additional schema properties from derived classes
  * @param {string} [dependencies] - additional dependencies from derived classes
  */
  constructor(id, cfg, cfg_schema, dependencies) {
    dependencies = _.union([/* put module specific dependencies here */], dependencies)
    cfg_schema = _.extend({
      workflow_server: { type: 'string', default: ':msvrDevices', description: 'Listener acting as the server-side for the workflow' },
      tabular: { type: 'boolean', description: 'if true, module will work in filter+table mode, otherwise in list mode', default: true },
    }, cfg_schema)
    super(id, cfg, cfg_schema, dependencies)
  } // constructor

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * used to return list of workflow processes
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {boolean} [params.full] - whether it is full or incremental update
  * @param {boolean} [params.limited] - indicates if data were limited to some fields only
  * @param {integer} [params.version] - version of data sent by server
  * @param {array} [params.sort] - sort criteria
  * @param {object} [params.query] - query parameters
  * @param {string} [params.type] - type of returned objects, e.g. id of workflow producing the objects
  * @param {integer} [params.total] - total number of objects matching the query
  * @param {integer} [params.offset] - offset of returned objects
  * @param {integer} [params.limit] - limits used in the query
  * @param {array} params.list - array of matching objects
  */
  mmObjects(sender, params) {
    if(params.type=='devices') {
      // when watcher update came, update translations of device id to device name
      if(params.full) {
        LANG.createOptions('device.name.', { '': LANG.ZERO_SPACE }, true)
      }

      params.list.forEach(device=>{
        LANG.createOptions('device.name.', { [device.id]: device.name })
      })

      try{
        document.body.forAll('.mele-list[auto][prefix="device.name."]', el=>el.autoChanged(true))
        document.body.forAll('.mele-input[prefix="device.name."]', el=>el.prefixChanged(el.prefix, el.prefix))
        document.body.forAll('mele-button[prefix="device.name."]', el=>el.prefixChanged(el.prefix, el.prefix))
        document.body.forAll('.mele-table.device-dependent', el=>el.rowattributesChanged())
      }catch(e) {}
    }
    super.mmObjects(sender, params)
  } // mmObjects

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Sends by &lt;mele-button> to parent module when a button is clicked
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.holder] - key attribute of button's parent container
  * @param {string} params.button - key attribute of the button
  * @param {integer} [params.count] - number of times button was clicked
  * @param {boolean} [params.long] - shows if press was long or not
  */
  async mmButtonClicked(sender, params) {
    if(params.button!='wpa') return super.mmButtonClicked(sender, params)

    this.popupWait(this.tr('PleaseWait'), this.tr('action.EXPORT'))
    try{
      const { params: { name, base64 } } = await this.request(this.config.workflow_server, 'mmGetExport', { format: 'wpa' }, 'mmExport')
      this.popupCloseDefault()
      this.createDownloadFromBase64('text/plain', name, base64)
    }catch(e) {
      this.popupError(String(e), e.name)
    }
  } // mmButtonClicked
} // mmodOfficeWfDevices
